import React, { useState, useEffect } from 'react';

import SuggestionDropdown from '../shared/SuggestionDropdown';
import { addItem, focusRef, getSuggestionsLocally } from '../shared/EventHelpers';
import { getIcon } from '../shared/FormatHelpers';

const filterAndMatchOptions = (options, currentValues, userInput) => {
    if (!options || !options.length || !currentValues) {
        return options;
    }
    const filteredOptions = options.filter((o) => !currentValues.some((v) => v.id === o.id));
    const matchedOptions = getSuggestionsLocally(filteredOptions, userInput);
    return matchedOptions;
};

const SelectAppender = ({ iconName, values, setValues, options, inputRef, onSave, autoFocus, handleCancel }) => {
    const [userInput, setUserInput] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [showOptions, setShowOptions] = useState(false);
    const icon = getIcon(iconName);

    useEffect(() => {
        autoFocus && focusRef(inputRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setFilteredOptions(filterAndMatchOptions(options, values, userInput));
        setActiveIndex(-1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInput, values, options]);

    const addSelectedItem = () => {
        if (activeIndex < 0 || activeIndex >= filteredOptions.length) {
            return;
        }
        addItem(values, setValues, filteredOptions[activeIndex]);
        setActiveIndex(-1);
        setUserInput('');
    };

    const scrollToIndex = (index) => {
        const divToScrollTo = document.getElementById(`suggestion-${index}`);
        if (divToScrollTo) {
            divToScrollTo.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    };

    const onMouseDown = (event) => {
        if (activeIndex >= 0) {
            addSelectedItem();
            event.preventDefault();
        }
    };

    const onKeyDown = (event) => {
        // Enter adds selected item or saves
        if (event.keyCode === 13) {
            if (activeIndex >= 0) {
                addSelectedItem();
                event.preventDefault();
            } else if (userInput === '') {
                setShowOptions(false);
                onSave && onSave();
            }
        }

        // Up-arrow moves up in list
        else if (event.keyCode === 38) {
            if (activeIndex > -1) {
                setActiveIndex(activeIndex - 1);
                scrollToIndex(activeIndex - 1);
            }
            event.preventDefault();
        }

        // Down-arrow moves down in list
        else if (event.keyCode === 40) {
            if (activeIndex < filteredOptions.length) {
                setActiveIndex(activeIndex + 1);
                scrollToIndex(activeIndex + 1);
            }
            event.preventDefault();
        }

        // Esc closes dropdown
        else if (event.keyCode === 27) {
            handleCancel();
            event.preventDefault();
        }
    };

    const onBlur = () => {
        setUserInput('');
        setShowOptions(false);
    };

    const onFocus = () => {
        setShowOptions(true);
    };

    return (
        <div className='new-item-selector'>
            <div className='d-flex flex-nowrap'>
                {icon}
                <input
                    ref={inputRef}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    onFocus={onFocus}
                />
            </div>
            <SuggestionDropdown
                {...{
                    userInput,
                    suggestions: filteredOptions,
                    showSuggestions: showOptions,
                    activeIndex,
                    setActiveIndex,
                    onMouseDown,
                    disableScroll: !filteredOptions || filteredOptions.length < 10,
                }}
            />
        </div>
    );
};

export default SelectAppender;
